import React, { useState, useEffect } from "react";
import {
  Users,
  UserPlus,
  Mail,
  Phone,
  Building,
  CheckCircle,
  MoreVertical,
  Search,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { ClientService } from "../../services/clients/clientService";
import { toast } from "react-hot-toast";

interface Relation {
  id: string;
  fournisseur: {
    id: string;
    raison_sociale: string;
    user_email?: string;
    siret?: string;
    telephone?: string;
  };
  statut: "actif" | "en_attente" | "rejete" | "suspendu";
  date_debut: string;
  nombre_factures: number;
  montant_total_factures: number;
}

export const ClientFournisseurs: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [showInviteModal, setShowInviteModal] = useState(searchParams.get("inviter") === "1");
  const [inviteEmail, setInviteEmail] = useState("");

  useEffect(() => {
    if (searchParams.has("inviter")) setSearchParams({}, { replace: true });
  }, []);
  const [inviteNom, setInviteNom] = useState("");
  const [relations, setRelations] = useState<Relation[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingInvite, setSendingInvite] = useState(false);

  const fetchFournisseurs = async () => {
    try {
      setLoading(true);
      const response = await ClientService.getClientFournisseurs();
      setRelations(response.data.results || []);
    } catch (error) {
      console.error("Erreur chargement fournisseurs", error);
      toast.error("Impossible de charger la liste des fournisseurs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFournisseurs();
  }, []);

  const filteredRelations = relations.filter(
    (rel) =>
      rel.fournisseur.raison_sociale?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rel.fournisseur.siret?.includes(searchTerm)
  );

  const handleInvite = async () => {
    if (!inviteEmail || !inviteNom) {
        toast.error("Veuillez remplir tous les champs");
        return;
    }

    try {
      setSendingInvite(true);
      await ClientService.inviteFournisseur({
        email: inviteEmail,
        nom_entreprise: inviteNom
      });
      toast.success(`Invitation envoyée à ${inviteNom} (${inviteEmail})`);
      setInviteEmail("");
      setInviteNom("");
      setShowInviteModal(false);
      // Optionally refresh list if the invitation creates a relation immediately (usually it doesn't until accepted)
    } catch (error) {
        console.error("Erreur invitation", error);
        toast.error("Erreur lors de l'envoi de l'invitation");
    } finally {
        setSendingInvite(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(amount);
  };

  if (loading && relations.length === 0) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  // Calcul stats
  const totalActifs = relations.filter(r => r.statut === 'actif').length;
  // Note: volume_total might not be on the relation object, depends on backend serializer.
  // If not available, we assume 0 for now.
  const volumeTotal = relations.reduce((acc, curr) => acc + (curr.montant_total_factures || 0), 0);
  const tauxActivation = relations.length > 0 ? Math.round((totalActifs / relations.length) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fournisseurs</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez vos relations avec les fournisseurs
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowInviteModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Inviter un fournisseur
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-lg bg-primary-100">
                  <Users className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Fournisseurs actifs
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">{totalActifs}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-lg bg-success-100">
                  <CheckCircle className="h-6 w-6 text-success-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Taux d'activation
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">{tauxActivation}%</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-lg bg-warning-100">
                  <span className="text-2xl font-bold text-warning-600">
                    XOF
                  </span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    volume total
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                     {new Intl.NumberFormat("fr-FR", {
                        maximumFractionDigits: 0,
                        notation: "compact", 
                        compactDisplay: "short"
                      }).format(volumeTotal)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recherche et filtres */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Rechercher un fournisseur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Liste des fournisseurs */}
        <div className="divide-y divide-gray-200">
          {filteredRelations.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    Aucun fournisseur trouvé. Invitez-en un pour commencer !
                </div>
          ) : (
          filteredRelations.map((rel) => (
            <div key={rel.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                      <Building className="h-6 w-6 text-primary-600" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center">
                      <h4 className="text-sm font-medium text-gray-900">
                        {rel.fournisseur.raison_sociale}
                      </h4>
                      <span
                        className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          rel.statut === "actif"
                            ? "bg-success-100 text-success-800"
                            : rel.statut === "en_attente"
                            ? "bg-warning-100 text-warning-800"
                            : "bg-error-100 text-error-800"
                        }`}
                      >
                        {rel.statut === "actif"
                          ? "Actif"
                          : rel.statut === "en_attente"
                          ? "En attente"
                          : rel.statut === "suspendu" 
                          ? "Suspendu"
                          : "Rejeté"}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center text-sm text-gray-500">
                        {rel.fournisseur.user_email && (
                            <>
                              <Mail className="h-4 w-4 mr-1" />
                              <span className="mr-4">{rel.fournisseur.user_email}</span>
                            </>
                        )}
                        {rel.fournisseur.telephone && (
                            <>
                                <Phone className="h-4 w-4 mr-1" />
                                <span>{rel.fournisseur.telephone}</span>
                            </>
                        )}
                    </div>
                    {rel.fournisseur.siret && (
                        <div className="mt-1 text-sm text-gray-500">
                        SIRET: {rel.fournisseur.siret}
                        </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {rel.nombre_factures || 0} factures
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatCurrency(rel.montant_total_factures || 0)}
                    </div>
                  </div>
                  <button className="p-2 text-gray-400 hover:text-gray-500">
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
          )}
        </div>
      </div>

      {/* Modal d'invitation */}
      {showInviteModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
              onClick={() => setShowInviteModal(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Inviter un fournisseur
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label
                          htmlFor="nom"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Nom de l'entreprise
                        </label>
                        <input
                          type="text"
                          id="nom"
                          value={inviteNom}
                          onChange={(e) => setInviteNom(e.target.value)}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                          placeholder="Nom de l'entreprise"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email du fournisseur
                        </label>
                        <input
                          type="email"
                          id="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                          placeholder="fournisseur@entreprise.com"
                        />
                      </div>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <p className="text-sm text-gray-600">
                          Un email d'invitation sera envoyé au fournisseur. Il
                          pourra ainsi créer son compte et commencer à soumettre
                          des factures.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleInvite}
                  disabled={sendingInvite}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {sendingInvite ? "Envoi..." : "Envoyer l'invitation"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  disabled={sendingInvite}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
